import React, { useEffect } from 'react';
/**
 * `RenewalsConfirmation` renders the confirmation step for the renewals
 * journey.
 *
 * Behaviour:
 * - Marks the confirmation step complete via `onPageChange`.
 * - Renders selected quotes using `RenewalsQuoteCard` in payment-completed
 *   mode, including totals/recurring messaging per payment method.
 * - For non-renewals journeys (e.g. call-centre), renders extra reference
 *   details instead of quote cards.
 */

import { Helmet } from 'react-helmet';
import { Journey, PageAbsolutePath } from '../../../consts/Common';
import Confirm from '../../common/Confirmation/Confirm';
import RenewalsQuoteCard from './quotes/RenewalsQuoteCard';
import { PaymentMethod } from '../../common/Constants';
import {
  getRenewalBenefits,
  getNoOfInstallments,
  getSelectedVariant,
  getProductInstalmentTotalPriceByPaymentMethod,
  getVariantInstalmentTotalPrice
} from '../../common/ProductMethods';
import { ProductType } from '../../common/CommonTypes';
import PageHeader from '../../common/PageHeader';
import { useJourney } from '../Layout';
import { useTranslation } from 'react-i18next';
import ConfirmationExtraDetails from '../../common/Confirmation/ConfirmationExtraDetails';
import { useAppSelector } from '../../../app-state/Hooks';

const RenewalsConfirmation = (): JSX.Element => {
  const { journey, onPageChange } = useJourney();
  const { products, personalDetail, orderNumbers } = journey;
  const appConfig = useAppSelector((s) => s.appConfig);
  const cms = useAppSelector((s) => s.content);

  const { confirmationPage } = cms;

  const { t } = useTranslation();

  const emailConfirmationText = confirmationPage.emailConfirmation.replace(
    '{EMAILADDRESS}',
    personalDetail.email
  );
  const referenceText = t('confirmation.your-reference', {
    orderNumbervalues: orderNumbers.join(' & '),
    interpolation: { escapeValue: false }
  });

  const noOfInstallments = getNoOfInstallments(products);

  useEffect(() => {
    window.scrollTo(0, 0);
    onPageChange(PageAbsolutePath.Confirmation, true);
  }, []);

  const renderQuotes = () => {
    return products
      .filter((q) => q.isSelected === true)
      .map((quote: ProductType) => {
        const selectedVariant = getSelectedVariant(quote.productVariants);

        if (selectedVariant) {
          const selectedLinkedProducts = quote.selectedLinkedProducts?.filter(
            (slp) => slp.quoteCardId === quote.productId
          );

          let subscriptionPrice = undefined;
          let singlePrice = undefined;
          let monthlyPrice = undefined;
          let totalPrice = undefined;

          if (selectedVariant.paymentMethod == PaymentMethod.Monthly) {
            const installmentPrice = getVariantInstalmentTotalPrice(
              quote.productId,
              selectedVariant,
              noOfInstallments,
              selectedLinkedProducts ?? [],
              quote.isVariablePrice
            );

            monthlyPrice = installmentPrice;

            totalPrice =
              installmentPrice.firstInstallmentPrice +
              installmentPrice.subsequentInstallmentPrice *
                (noOfInstallments - 1);
          } else if (
            selectedVariant.paymentMethod == PaymentMethod.Subscription
          ) {
            subscriptionPrice = getProductInstalmentTotalPriceByPaymentMethod(
              quote,
              PaymentMethod.Subscription,
              noOfInstallments,
              selectedLinkedProducts || [],
              quote.selectedCoverDuration ?? 0,
              quote.selectedExcess ?? 0
            );
          } else {
            singlePrice = getProductInstalmentTotalPriceByPaymentMethod(
              quote,
              PaymentMethod.Single,
              noOfInstallments,
              selectedLinkedProducts || [],
              quote.selectedCoverDuration ?? 0,
              quote.selectedExcess ?? 0
            );
          }

          return (
            <RenewalsQuoteCard
              key={`quote-${quote.productTypeId}-${quote.renewalGroupId}`}
              id={quote.renewalGroupId}
              productId={quote.productId}
              keyBenefitLists={getRenewalBenefits(
                cms.quotePage.productTypeCustomisations,
                quote,
                selectedLinkedProducts
              )}
              quoteTitle={selectedVariant.customerFriendlyName}
              quoteType={quote.productTypeId}
              priceMonthly={monthlyPrice}
              priceTotal={totalPrice}
              priceSingle={singlePrice?.firstInstallmentPrice}
              priceSubscription={subscriptionPrice?.firstInstallmentPrice}
              selectedOptionalLinkedQuotes={selectedLinkedProducts}
              linkedQuotes={selectedVariant.linkedProducts}
              isPaymentDone={true}
              showMoreInfo={true}
              showSelectOption={false}
              noOfInstallments={noOfInstallments}
              selectedQuote={quote.productId}
              productVariants={quote.productVariants}
              showAllPricingOptions={false}
              isVariablePrice={quote.isVariablePrice}
              culture={appConfig.cultureCode}
              selectedCoverDuration={quote.selectedCoverDuration ?? 0}
              selectedExcess={quote.selectedExcess}
            />
          );
        }
      });
  };

  const nonce =
    document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content') ??
    '';

  return (
    <>
      <Helmet>
        <title>{t('page-title.confirmation')}</title>
        <script nonce={nonce} type='text/javascript'>
          {confirmationPage.force24Script}
        </script>
      </Helmet>

      <PageHeader
        title={confirmationPage.title}
        description={confirmationPage.description}
        information={confirmationPage.information}
        warning={confirmationPage.warning}
        descriptionClass='p2'
      />
      <Confirm
        subTitle={confirmationPage.summaryDescription}
        emailConfirmationText={emailConfirmationText}
        orderNumbers={orderNumbers}
        referenceText={referenceText}>
        {appConfig.journeyType === Journey.Renewals ? (
          renderQuotes()
        ) : (
          <ConfirmationExtraDetails referenceText={referenceText} />
        )}
      </Confirm>
    </>
  );
};

export default RenewalsConfirmation;
